/**
 * KAGRA Budget Platform - Tariffs & Calculation Engine
 * Grounded in NSTCCore / ASGC 2026 Official Rates and KAGRA DocDB 17608
 */
import rawModelsData from './data/models_150m.json' with { type: 'json' };

export const DEFAULT_BUDGET_NTD = 1500000.0;

export const STORAGE_TARIFFS = {
  tape: {
    unit: 'TB',
    rate_yearly: 300.0,
    rate_daily: 300.0 / 365.0,
    name_zh: 'LTO 磁帶保證保留容量',
    name_en: 'LTO Tape Guaranteed Retention',
    name_ja: 'LTO テープ保証保管容量',
    min: 0,
    max: 2500,
    step: 50,
    default: 1000
  },
  ceph: {
    unit: 'TB',
    rate_yearly: 1000.0,
    rate_daily: 1000.0 / 365.0,
    name_zh: 'CephFS 高速快取磁碟',
    name_en: 'CephFS Scratch Cache Disk',
    name_ja: 'CephFS 高速ワークキャッシュ',
    min: 0,
    max: 500,
    step: 10,
    default: 200
  },
  osdf_disk: {
    unit: 'TB',
    rate_yearly: 1000.0,
    rate_daily: 1000.0 / 365.0,
    min: 10,
    max: 50,
    step: 5,
    default: 10
  },
  osdf_vcpu: {
    unit: 'vCPU',
    min: 2,
    max: 16,
    step: 2,
    default: 4
  }
};

export const CPU_MODELS = {
  EDR2: {
    id: 'EDR2',
    name: 'AMD EPYC 9645 (Zen 5c)',
    cores_per_node: 96,
    per_core_day: 1.2,
    per_core_year: 438.0,
    badge: 'Recommended / 最高能效'
  },
  intel_g4: {
    id: 'intel_g4',
    name: 'Intel Xeon Gold 6448H',
    cores_per_node: 128,
    per_core_day: 1.4,
    per_core_year: 511.0,
    badge: 'Intel MKL Optimized'
  },
  EDR1: {
    id: 'EDR1',
    name: 'AMD Genoa 9654 (Zen 4)',
    cores_per_node: 192,
    per_core_day: 1.2,
    per_core_year: 438.0,
    badge: 'Standard Baseline'
  },
  HDR1: {
    id: 'HDR1',
    name: 'AMD Rome 7662 (Zen 2)',
    cores_per_node: 128,
    per_core_day: 1.0,
    per_core_year: 365.0,
    badge: 'Economical Batch'
  }
};

export const GPU_MODELS = {
  A100: {
    id: 'A100',
    name: 'NVIDIA A100 (80GB)',
    vram_gb: 80,
    per_card_day: 120.0,
    per_card_year: 43800.0,
    max_cards: 32,
    stock: 24,
    arch: 'Ampere Tensor Core',
    best_for: 'FP64 / O5 ML Search'
  },
  L40S: {
    id: 'L40S',
    name: 'NVIDIA L40S (45GB)',
    vram_gb: 45,
    per_card_day: 70.0,
    per_card_year: 25550.0,
    max_cards: 32,
    stock: 4,
    arch: 'Ada Lovelace Pro',
    best_for: 'GW Transformer / High-Throughput'
  },
  RTX4090: {
    id: 'RTX4090',
    name: 'NVIDIA RTX 4090 (24GB)',
    vram_gb: 24,
    per_card_day: 60.0,
    per_card_year: 21900.0,
    max_cards: 32,
    stock: 16,
    arch: 'Ada Lovelace Flagship',
    best_for: 'FP32 Matched Filtering / Fast Iteration'
  },
  RTX3090: {
    id: 'RTX3090',
    name: 'NVIDIA RTX 3090 (24GB)',
    vram_gb: 24,
    per_card_day: 40.0,
    per_card_year: 14600.0,
    max_cards: 32,
    stock: 32,
    arch: 'Ampere High Density',
    best_for: 'Low-latency Real-time Inference'
  },
  V100: {
    id: 'V100',
    name: 'NVIDIA Tesla V100 (24GB)',
    vram_gb: 24,
    per_card_day: 35.0,
    per_card_year: 12775.0,
    max_cards: 32,
    stock: 48,
    arch: 'Volta Classic',
    best_for: 'Legacy CUDA / FP64 Validation'
  },
  B6000: {
    id: 'B6000',
    name: 'NVIDIA RTX PRO 6000 (96GB)',
    vram_gb: 96,
    per_card_day: 127.0,
    per_card_year: 46355.0,
    max_cards: 32,
    stock: 16,
    arch: 'Ada Lovelace Ultra-VRAM',
    best_for: 'Massive Waveform Template Banks'
  },
  P100: {
    id: 'P100',
    name: 'NVIDIA Tesla P100 (16GB)',
    vram_gb: 16,
    per_card_day: 8.0,
    per_card_year: 2920.0,
    max_cards: 32,
    stock: 16,
    arch: 'Pascal Datacenter',
    best_for: 'Syntax Checks / Lightweight Testbench'
  }
};

export const PRESETS = {
  preset_1: {
    id: 'preset_1',
    key: 'flagship_gpu',
    name_zh: '方案一：旗艦 GPU 專案專用型',
    name_en: 'Preset 1: Flagship GPU Dedicated',
    name_ja: 'プリセット1：フラッグシップ GPU 専有型',
    subtitle_zh: 'Sawada / RESCEU 即時搜尋加速專案',
    subtitle_en: 'Sawada / RESCEU Real-Time Search',
    subtitle_ja: 'Sawada / RESCEU リアルタイム探査',
    tag_zh: '16 卡 A100 (80GB) ＋ 656 核 CPU',
    tag_en: '16x A100 (80GB) + 656 Cores',
    tag_ja: 'A100 (80GB)×16枚 ＋ 656コア',
    config: {
      tape_tb: 1000,
      disk_tb: 200,
      osdf_disk_tb: 10,
      osdf_vcpu: 4,
      gpu_allocations: {
        A100: 16,
        L40S: 0,
        RTX4090: 0,
        RTX3090: 0,
        V100: 0,
        B6000: 0,
        P100: 0
      },
      cpu_type: 'EDR2',
      cpu_cores: 656
    }
  },
  preset_2: {
    id: 'preset_2',
    key: 'balanced_flagship',
    name_zh: '方案二：均衡旗艦型',
    name_en: 'Preset 2: Balanced Flagship',
    name_ja: 'プリセット2：バランス旗艦型',
    subtitle_zh: '即時推論 ＋ 千核大規模離線參數估計 (PE)',
    subtitle_en: 'Real-Time Inference + 1,250 Cores PE',
    subtitle_ja: 'リアルタイム推論 ＋ 千コア規模 PE',
    tag_zh: '10 卡 A100 (80GB) ＋ 1,252 核 CPU',
    tag_en: '10x A100 (80GB) + 1,252 Cores',
    tag_ja: 'A100 (80GB)×10枚 ＋ 1,252コア',
    config: {
      tape_tb: 1000,
      disk_tb: 200,
      osdf_disk_tb: 10,
      osdf_vcpu: 8,
      gpu_allocations: {
        A100: 10,
        L40S: 0,
        RTX4090: 0,
        RTX3090: 0,
        V100: 0,
        B6000: 0,
        P100: 0
      },
      cpu_type: 'EDR2',
      cpu_cores: 1252
    }
  },
  preset_3: {
    id: 'preset_3',
    key: 'high_density',
    name_zh: '方案三：高卡數高性價比型',
    name_en: 'Preset 3: High-Density High-Throughput',
    name_ja: 'プリセット3：高密度・高コスパ型',
    subtitle_zh: 'L40S / RTX 4090 巨量並發與多青年團隊支持',
    subtitle_en: '24 GPUs Concurrency + 949 Cores',
    subtitle_ja: '24枚GPU並列 ＋ 949コア',
    tag_zh: '12 卡 L40S ＋ 12 卡 4090 ＋ 929~949 核',
    tag_en: '12x L40S + 12x 4090 + 929~949 Cores',
    tag_ja: 'L40S×12枚 ＋ 4090×12枚 ＋ 929~949コア',
    config: {
      tape_tb: 1000,
      disk_tb: 200,
      osdf_disk_tb: 20,
      osdf_vcpu: 8,
      gpu_allocations: {
        A100: 0,
        L40S: 12,
        RTX4090: 12,
        RTX3090: 0,
        V100: 0,
        B6000: 0,
        P100: 0
      },
      cpu_type: 'EDR2',
      cpu_cores: 929
    }
  },
  preset_4: {
    id: 'preset_4',
    key: 'full_stack_package',
    name_zh: '方案四：完整科學套裝型',
    name_en: 'Preset 4: Full-Stack Science Package',
    name_ja: 'プリセット4：フルスタック・サイエンスパッケージ',
    subtitle_zh: '頂級混合 GPU ＋ 千核常駐 ＋ 20 TB OSDF 樞紐',
    subtitle_en: 'Hybrid GPUs + 1,170 Cores + 20TB OSDF',
    subtitle_ja: 'ハイブリッドGPU ＋ 1,170コア ＋ 20TB OSDF',
    tag_zh: '6 卡 A100 ＋ 8 卡 L40S ＋ 1,162~1,170 核 ＋ 20 TB OSDF',
    tag_en: '6x A100 + 8x L40S + 1,162~1,170 Cores + 20TB OSDF',
    tag_ja: 'A100×6枚 ＋ L40S×8枚 ＋ 1,162~1,170コア ＋ 20TB OSDF',
    config: {
      tape_tb: 1000,
      disk_tb: 200,
      osdf_disk_tb: 20,
      osdf_vcpu: 8,
      gpu_allocations: {
        A100: 6,
        L40S: 8,
        RTX4090: 0,
        RTX3090: 0,
        V100: 0,
        B6000: 0,
        P100: 0
      },
      cpu_type: 'EDR2',
      cpu_cores: 1162
    }
  }
};

function sanitizeNumber(val, fallback, min = 0, max = Infinity) {
  const n = Number(val);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

/**
 * Perform comprehensive budget and allocation calculation
 * @param {Object} state Current configuration
 * @returns {Object} Full breakdown of costs, resources, and validation status
 */
export function calculateAllocation(state) {
  const totalBudget = sanitizeNumber(state.budget, DEFAULT_BUDGET_NTD, 0);
  const tapeTb = sanitizeNumber(state.tape_tb, 1000, 0);
  const diskTb = sanitizeNumber(state.disk_tb, 200, 0);
  const osdfDiskTb = sanitizeNumber(state.osdf_disk_tb, 10, 0);
  const osdfVcpu = Math.round(sanitizeNumber(state.osdf_vcpu, 4, 1));
  const cpuType = state.cpu_type && CPU_MODELS[state.cpu_type] ? state.cpu_type : 'EDR2';
  const cpuCores = Math.round(sanitizeNumber(state.cpu_cores, 0, 0));

  // Storage Costs
  const tapeCost = tapeTb * STORAGE_TARIFFS.tape.rate_yearly;
  const diskCost = diskTb * STORAGE_TARIFFS.ceph.rate_yearly;

  // OSDF Costs (using selected CPU rate for vCPU proxy)
  const cpuRate = CPU_MODELS[cpuType] ? CPU_MODELS[cpuType].per_core_year : 438.0;
  const osdfDiskCost = osdfDiskTb * STORAGE_TARIFFS.ceph.rate_yearly;
  const osdfCpuCost = osdfVcpu * cpuRate;
  const osdfTotalCost = osdfDiskCost + osdfCpuCost;

  // GPU Costs
  const gpuAllocations = state.gpu_allocations || {};
  let totalGpuCost = 0;
  let totalGpuCards = 0;
  let totalVramGb = 0;
  const gpuDetails = [];

  for (const [modelKey, modelInfo] of Object.entries(GPU_MODELS)) {
    const count = Math.max(0, parseInt(gpuAllocations[modelKey] || 0, 10));
    if (count > 0) {
      const cardCost = count * modelInfo.per_card_year;
      const vram = count * modelInfo.vram_gb;
      totalGpuCost += cardCost;
      totalGpuCards += count;
      totalVramGb += vram;
      gpuDetails.push({
        id: modelKey,
        name: modelInfo.name,
        count,
        vram_gb_single: modelInfo.vram_gb,
        vram_gb_total: vram,
        per_card_year: modelInfo.per_card_year,
        total_cost: cardCost
      });
    }
  }

  // CPU Costs
  const cpuCost = cpuCores * cpuRate;
  const cpuCoreDays = cpuCores * 365;

  // Aggregate
  const storageSubtotal = tapeCost + diskCost + osdfDiskCost;
  const computeSubtotal = totalGpuCost + cpuCost + osdfCpuCost;
  const totalCost = tapeCost + diskCost + osdfTotalCost + totalGpuCost + cpuCost;
  const remainingBalance = totalBudget - totalCost;
  const utilizationPct = totalBudget > 0 ? (totalCost / totalBudget) * 100 : 0;
  const gpuSharePct = totalBudget > 0 ? (totalGpuCost / totalBudget) * 100 : 0;
  const cpuSharePct = totalBudget > 0 ? (cpuCost / totalBudget) * 100 : 0;
  const computeSharePct = totalBudget > 0 ? (computeSubtotal / totalBudget) * 100 : 0;

  // Strategic validation checks
  const isValid = totalCost <= totalBudget;
  const isOptimal = isValid && remainingBalance >= 0 && remainingBalance < cpuRate; // No funds wasted to buy another core
  const gpuTargetMet = computeSharePct >= 64.9 && totalGpuCost > 0; // Wang's strategic directive: >65% compute transformation with dedicated GPU foundation
  const retentionCompliant = tapeTb <= 1000.0 && diskTb <= 200.0; // Complies with DocDB 17608 1.0 PB Tape cap

  return {
    totalBudget,
    totalCost,
    remainingBalance,
    utilizationPct,
    isValid,
    isOptimal,
    gpuTargetMet,
    retentionCompliant,
    tape: {
      tb: tapeTb,
      pb: tapeTb / 1000.0,
      unitPrice: STORAGE_TARIFFS.tape.rate_yearly,
      cost: tapeCost,
      pct: totalBudget > 0 ? (tapeCost / totalBudget) * 100 : 0
    },
    disk: {
      tb: diskTb,
      pb: diskTb / 1000.0,
      unitPrice: STORAGE_TARIFFS.ceph.rate_yearly,
      cost: diskCost,
      pct: totalBudget > 0 ? (diskCost / totalBudget) * 100 : 0
    },
    osdf: {
      diskTb: osdfDiskTb,
      vcpu: osdfVcpu,
      cpuType,
      diskCost: osdfDiskCost,
      cpuCost: osdfCpuCost,
      totalCost: osdfTotalCost,
      pct: totalBudget > 0 ? (osdfTotalCost / totalBudget) * 100 : 0
    },
    gpu: {
      details: gpuDetails,
      totalCards: totalGpuCards,
      totalVramGb,
      totalCost: totalGpuCost,
      pct: gpuSharePct
    },
    cpu: {
      type: cpuType,
      name: CPU_MODELS[cpuType] ? CPU_MODELS[cpuType].name : cpuType,
      cores: cpuCores,
      coreDays: cpuCoreDays,
      unitPrice: cpuRate,
      cost: cpuCost,
      pct: cpuSharePct
    },
    storageSubtotal: {
      cost: storageSubtotal,
      pct: totalBudget > 0 ? (storageSubtotal / totalBudget) * 100 : 0
    },
    computeSubtotal: {
      cost: computeSubtotal,
      pct: totalBudget > 0 ? (computeSubtotal / totalBudget) * 100 : 0
    }
  };
}

/**
 * Calculate the exact CPU cores that can be purchased with remaining balance
 * @param {Object} state
 * @returns {number}
 */
export function calculateAutoFillCores(state) {
  const totalBudget = sanitizeNumber(state.budget, DEFAULT_BUDGET_NTD, 0);
  const tapeTb = sanitizeNumber(state.tape_tb, 1000, 0);
  const diskTb = sanitizeNumber(state.disk_tb, 200, 0);
  const osdfDiskTb = sanitizeNumber(state.osdf_disk_tb, 10, 0);
  const osdfVcpu = Math.round(sanitizeNumber(state.osdf_vcpu, 4, 1));
  const cpuType = state.cpu_type && CPU_MODELS[state.cpu_type] ? state.cpu_type : 'EDR2';
  const cpuRate = CPU_MODELS[cpuType] ? CPU_MODELS[cpuType].per_core_year : 438.0;

  const tapeCost = tapeTb * STORAGE_TARIFFS.tape.rate_yearly;
  const diskCost = diskTb * STORAGE_TARIFFS.ceph.rate_yearly;
  const osdfTotalCost = (osdfDiskTb * STORAGE_TARIFFS.ceph.rate_yearly) + (osdfVcpu * cpuRate);

  let totalGpuCost = 0;
  const gpuAllocations = state.gpu_allocations || {};
  for (const [modelKey, modelInfo] of Object.entries(GPU_MODELS)) {
    const count = Math.max(0, parseInt(gpuAllocations[modelKey] || 0, 10));
    totalGpuCost += count * modelInfo.per_card_year;
  }

  const committedCostWithoutCpu = tapeCost + diskCost + osdfTotalCost + totalGpuCost;
  const availableForCpu = totalBudget - committedCostWithoutCpu;

  if (availableForCpu <= 0 || cpuRate <= 0) {
    return 0;
  }

  return Math.floor(availableForCpu / cpuRate);
}
